import { useEffect } from 'react';
import type { RouteDefinition } from '@/routes';
import { applyRouteMeta } from '@/lib/meta';
import { AppLayout } from '@/layout/AppLayout';
import { RouteNextSteps } from '@/components/content/RouteNextSteps';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { changelogEntries } from '@/content/changelog';

type Props = {
  route: RouteDefinition;
};

type EntryListProps = {
  label: string;
  items?: string[];
};

function EntryList({ label, items }: EntryListProps) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <p className="inline-flex rounded-full bg-secondary px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-secondary-foreground">
        {label}
      </p>
      <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

export function ChangelogPage({ route }: Props) {
  useEffect(() => {
    applyRouteMeta(route);
  }, [route]);

  return (
    <AppLayout>
      <section className="hero-shell rounded-2xl border border-border p-6 md:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Release Notes</p>
        <h1 className="mt-2 font-display text-4xl leading-tight md:text-5xl">Changelog</h1>
        <p className="mt-3 max-w-3xl text-sm text-muted-foreground md:text-base">
          Product updates, fixes, and improvements across calculators, exports, and supporting pages.
        </p>
      </section>

      <section className="space-y-4">
        {changelogEntries.map((entry) => (
          <Card key={entry.version}>
            <CardHeader>
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-md border border-border bg-card px-2 py-1 text-xs font-semibold">{entry.version}</span>
                <span className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">{entry.date}</span>
              </div>
              <CardTitle>{entry.title}</CardTitle>
              <CardDescription>{route.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <EntryList label="Added" items={entry.added} />
              <EntryList label="Improved" items={entry.improved} />
              <EntryList label="Fixed" items={entry.fixed} />
            </CardContent>
          </Card>
        ))}
      </section>

      <RouteNextSteps canonicalPath={route.canonicalPath} />
    </AppLayout>
  );
}
